import React from 'react';
import { motion } from 'framer-motion';
import { Phone, FileText, PauseCircle, AlertCircle, Clock, User } from 'lucide-react';
import { Loan } from '../data/mockLoans';
interface ActiveLoanCardProps {
  loan: Loan;
  onActionClick: (action: 'calling' | 'noting' | 'snoozing') => void;
  historyCount: number;
  onHistoryClick: () => void;
}
export function ActiveLoanCard({
  loan,
  onActionClick,
  historyCount,
  onHistoryClick
}: ActiveLoanCardProps) {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  return <motion.div key={loan.id} initial={{
    opacity: 0,
    x: 50
  }} animate={{
    opacity: 1,
    x: 0
  }} exit={{
    opacity: 0,
    x: -50
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 30
  }} className="w-full max-w-3xl bg-white rounded-2xl shadow-xl border border-stone-200 overflow-hidden flex flex-col" data-id="element-1">
      {/* Header */}
      <div className="p-8 text-white" style={{
      backgroundColor: '#2A2A2A'
    }} data-id="element-2">
        <div className="flex justify-between items-start mb-6" data-id="element-3">
          <div data-id="element-4">
            <div className="flex items-center gap-3 mb-2" data-id="element-5">
              <span className="text-xs font-bold px-2.5 py-1 rounded-md uppercase tracking-wider" style={{
              backgroundColor: 'rgba(197, 165, 90, 0.2)',
              color: '#D4B96A',
              borderWidth: 1,
              borderColor: 'rgba(197, 165, 90, 0.3)'
            }} data-id="element-6">
                {loan.loanType}
              </span>
              <span className="text-stone-400 text-sm font-medium" data-id="element-7">
                {loan.id}
              </span>
            </div>
            <h1 className="text-3xl font-bold flex items-center gap-3" data-id="element-8">
              <User className="w-8 h-8 text-stone-400" data-id="element-9" />
              {loan.borrowerName}
            </h1>
          </div>
          <div className="flex flex-col items-end gap-3" data-id="element-10">
            <div className="text-right" data-id="element-11">
              <p className="text-stone-400 text-sm font-medium mb-1" data-id="element-12">
                Loan Amount
              </p>
              <p className="text-2xl font-bold" style={{
              color: '#C5A55A'
            }} data-id="element-13">
                {formatCurrency(loan.loanAmount)}
              </p>
            </div>
            <button onClick={onHistoryClick} className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/10 active:scale-[0.97]" style={{
            backgroundColor: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(255,255,255,0.12)'
          }} data-id="element-14">
              <Clock className="w-3.5 h-3.5" style={{
              color: '#C5A55A'
            }} data-id="element-15" />
              <span className="text-stone-300" data-id="element-16">History</span>
              {historyCount > 0 && <span className="text-xs font-bold px-1.5 py-0.5 rounded-full text-white min-w-[20px] text-center" style={{
              backgroundColor: '#C5A55A',
              fontSize: '10px'
            }} data-id="element-17">
                  {historyCount}
                </span>}
            </button>
          </div>
        </div>

        {/* Big Phone Number */}
        <div className="inline-flex items-center gap-3 rounded-xl px-6 py-4" style={{
        backgroundColor: 'rgba(255,255,255,0.06)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.1)'
      }} data-id="element-18">
          <Phone className="w-6 h-6" style={{
          color: '#C5A55A'
        }} data-id="element-19" />
          <a href={`tel:${loan.phoneNumber}`} className="text-3xl font-bold tracking-tight transition-colors hover:opacity-80" style={{
          color: '#C5A55A'
        }} data-id="element-20">
            {loan.phoneNumber}
          </a>
        </div>
      </div>

      {/* Body */}
      <div className="p-8 flex-1 grid grid-cols-1 md:grid-cols-2 gap-8 bg-stone-50" data-id="element-21">
        {/* Conditions */}
        <div className="flex flex-col min-h-0" data-id="element-22">
          <h2 className="text-sm font-bold text-stone-900 uppercase tracking-wider mb-4 flex items-center gap-2 flex-shrink-0" data-id="element-23">
            <AlertCircle className="w-4 h-4 text-rose-500" data-id="element-24" />
            Outstanding Conditions ({loan.outstandingConditions.length})
          </h2>
          <ul className="space-y-3 overflow-y-auto max-h-64 pr-1" style={{
          scrollbarWidth: 'thin',
          scrollbarColor: '#d6d3d1 transparent'
        }} data-id="element-25">
            {loan.outstandingConditions.map(condition => <li key={condition.id} className="bg-white p-4 rounded-xl border border-stone-200 shadow-sm flex items-start gap-3" data-id="element-26">
                <div className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${condition.priority === 'high' ? 'bg-rose-500' : condition.priority === 'medium' ? 'bg-amber-500' : 'bg-stone-400'}`} data-id="element-27" />
                <span className="text-sm text-stone-700 leading-snug" data-id="element-28">
                  {condition.description}
                </span>
              </li>)}
          </ul>
        </div>

        {/* Recent Note */}
        <div data-id="element-29">
          <h2 className="text-sm font-bold text-stone-900 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-30">
            <Clock className="w-4 h-4 text-stone-500" data-id="element-31" />
            Most Recent Note
          </h2>
          <div className="bg-white p-5 rounded-xl border border-stone-200 shadow-sm relative" data-id="element-32">
            <div className="absolute top-0 left-0 w-1 h-full rounded-l-xl" style={{
            backgroundColor: '#C5A55A'
          }} data-id="element-33"></div>
            <p className="text-stone-700 text-sm italic mb-4 leading-relaxed" data-id="element-34">
              "{loan.mostRecentNote.text}"
            </p>
            <div className="flex justify-between items-center text-xs text-stone-500 font-medium" data-id="element-35">
              <span data-id="element-36">{loan.mostRecentNote.author}</span>
              <span data-id="element-37">{formatDate(loan.mostRecentNote.date)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Action Footer */}
      <div className="p-6 bg-white border-t border-stone-200 flex gap-4" data-id="element-38">
        <button onClick={() => onActionClick('calling')} className="flex-1 text-white py-4 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all shadow-sm hover:shadow-md active:scale-[0.98]" style={{
        backgroundColor: '#C5A55A'
      }} onMouseEnter={e => e.currentTarget.style.backgroundColor = '#B8963E'} onMouseLeave={e => e.currentTarget.style.backgroundColor = '#C5A55A'} data-id="element-39">
          <Phone className="w-5 h-5" data-id="element-40" />
          Call Borrower
        </button>
        <button onClick={() => onActionClick('noting')} className="flex-1 bg-stone-100 hover:bg-stone-200 text-stone-800 py-4 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all border border-stone-200 active:scale-[0.98]" data-id="element-41">
          <FileText className="w-5 h-5" data-id="element-42" />
          Leave Note
        </button>
        <button onClick={() => onActionClick('snoozing')} className="flex-1 bg-amber-50 hover:bg-amber-100 text-amber-700 py-4 px-6 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all border border-amber-200 active:scale-[0.98]" data-id="element-43">
          <PauseCircle className="w-5 h-5" data-id="element-44" />
          Snooze
        </button>
      </div>
    </motion.div>;
}